import { useMemo, useState } from 'react'
import { Building2, Check, Loader2, Plus } from 'lucide-react'
import { toast } from 'sonner'
import { useCreateManufacturer, useManufacturers } from '@/api/hooks'
import type { components } from '@/api/schema'
import {
  PickerMenu,
  PickerMenuContent,
  PickerMenuItem,
  PickerMenuList,
  PickerMenuSearch,
  PickerMenuSeparator,
  PickerMenuTrigger,
} from '@/components/picker-menu'
import { cn } from '@/lib/utils'

export type ManufacturerAssignment = components['schemas']['ManufacturerRead']

/**
 * Searchable canonical-manufacturer picker shared by import review surfaces.
 * `allowCreate` adds a manufacturer AQ does not carry from the search text.
 */
export function ManufacturerAssignmentPicker({
  currentId,
  currentName,
  pending = false,
  allowCreate = false,
  onChange,
  className,
}: {
  currentId?: number | null
  currentName?: string | null
  pending?: boolean
  allowCreate?: boolean
  onChange: (manufacturer: ManufacturerAssignment) => void
  className?: string
}) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const manufacturers = useManufacturers({
    limit: 100,
    status: 'all',
    sort_by: 'name',
    search: search.trim() || undefined,
  })
  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase()
    const items = manufacturers.data?.items ?? []
    if (!query) return items
    return items.filter(
      (manufacturer) =>
        manufacturer.name.toLowerCase().includes(query) ||
        manufacturer.short_name.toLowerCase().includes(query) ||
        manufacturer.source_id.toLowerCase().includes(query),
    )
  }, [manufacturers.data?.items, search])
  const createManufacturer = useCreateManufacturer()
  const query = search.trim().replace(/\s+/g, ' ')
  const exact = filtered.some((manufacturer) => manufacturer.name.toLowerCase() === query.toLowerCase())
  const choose = (manufacturer: ManufacturerAssignment) => {
    setOpen(false)
    setSearch('')
    onChange(manufacturer)
  }

  return (
    <PickerMenu
      open={open}
      onOpenChange={(nextOpen) => {
        if (pending) return
        setOpen(nextOpen)
        // An unmatched line opens on its imported name, so a missing manufacturer is one click to add.
        setSearch(nextOpen && !currentId && allowCreate ? (currentName ?? '').trim() : '')
      }}
    >
      <PickerMenuTrigger asChild disabled={pending}>
        <button
          type="button"
          aria-label={`Manufacturer: ${currentName || 'needs review'}`}
          aria-busy={pending}
          className={cn(
            'inline-flex min-h-8 max-w-full items-center gap-1.5 rounded-md px-2 py-1 text-left text-sm font-semibold transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50 disabled:opacity-60',
            !currentId && 'text-amber-700',
            className,
          )}
        >
          {pending ? <Loader2 className="size-3.5 animate-spin" /> : <Building2 className="size-3.5" />}
          <span className="truncate">{currentName || 'Choose manufacturer'}</span>
        </button>
      </PickerMenuTrigger>
      <PickerMenuContent title="Canonical manufacturer" align="start" className="w-80">
        <PickerMenuSearch
          value={search}
          onChange={setSearch}
          placeholder="Search manufacturers"
          ariaLabel="Search manufacturers"
        />
        <PickerMenuList className="max-h-72">
          {manufacturers.isLoading ? (
            <div className="flex min-h-24 items-center justify-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="size-4 animate-spin" /> Loading manufacturers
            </div>
          ) : filtered.length ? (
            filtered.map((manufacturer) => (
              <PickerMenuItem
                key={manufacturer.uuid}
                disabled={manufacturer.id === currentId}
                className="min-h-11 justify-between"
                onSelect={() => choose(manufacturer)}
              >
                <span className="min-w-0">
                  <span className="block truncate font-medium">{manufacturer.name}</span>
                  <span className="block truncate text-xs text-muted-foreground">
                    {manufacturer.source === 'auto_quotes_api'
                      ? `AQ ID ${manufacturer.source_id}`
                      : 'Added manually'}
                  </span>
                </span>
                {manufacturer.id === currentId ? <Check className="size-4 text-primary" /> : null}
              </PickerMenuItem>
            ))
          ) : (
            <p className="px-3 py-6 text-center text-sm text-muted-foreground">No matching manufacturer</p>
          )}
          {allowCreate && query && !exact && !manufacturers.isLoading && (
            <>
              <PickerMenuSeparator />
              <PickerMenuItem
                disabled={createManufacturer.isPending}
                className="min-h-11"
                onSelect={(event) => {
                  event.preventDefault()
                  createManufacturer.mutate(query, {
                    onSuccess: (manufacturer) => choose(manufacturer),
                    onError: () => toast.error(`Could not add ${query}`),
                  })
                }}
              >
                {createManufacturer.isPending ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <Plus className="size-4 text-muted-foreground" />
                )}
                <span className="min-w-0">
                  <span className="block truncate font-medium">Add “{query}”</span>
                  <span className="block truncate text-xs text-muted-foreground">
                    New manufacturer, not in AQ
                  </span>
                </span>
              </PickerMenuItem>
            </>
          )}
        </PickerMenuList>
      </PickerMenuContent>
    </PickerMenu>
  )
}
